import { SessionManager } from '@pp/toolkit';

import { snakeToCamel, type CamelCaseKeys } from '../api/converters/snakeToCamel';
import { LocalStorage } from '../storage/local/LocalStorage';
import { SessionStorage } from '../storage/session/SessionStorage';

export const defaultRawSession: IUserSessionRaw = {
  JSESSIONID: '',
  actual_web_server: '',
  casino_id: '',
  config_url: '',
  gamePath: '',
  game_mode: '',
  gametype: '',
  hasQuickGuide: '',
  hostCommunicationVersion: '',
  integration_api: '',
  lang: '',
  lobbyFilter: '',
  meta_server: '',
  operator_theme: '',
  socket_port: '',
  socket_server: '',
  stats_collector_uuid: '',
  table_id: '',
  token: '',
  uiversion: '',
  userId: '',
  web_server: '',
};

export interface IUserSessionRaw extends Record<string, string> {
  JSESSIONID: string;
  actual_web_server: string;
  casino_id: string;
  config_url: string;
  gamePath: string;
  game_mode: string;
  gametype: string;
  hasQuickGuide: string;
  hostCommunicationVersion: string;
  integration_api: string;
  lang: string;
  lobbyFilter: string;
  meta_server: string;
  operator_theme: string;
  socket_port: string;
  socket_server: string;
  stats_collector_uuid: string;
  table_id: string;
  token: string;
  uiversion: string;
  userId: string;
  web_server: string;
}

export interface IUserSession extends CamelCaseKeys<IUserSessionRaw>, Record<string, string> {}

const isProduction = process.env.NODE_ENV === 'production';

class CoreSessionManager extends SessionManager {
  readonly session: IUserSessionRaw | null;
  readonly parsedSession: IUserSession | null;

  constructor() {
    super(SessionStorage, LocalStorage);

    this.session = this.getSession();
    this.parsedSession = this.getParsedSession();
  }

  getSession(): IUserSessionRaw | null {
    const rawSession = super.getSession();
    return { ...defaultRawSession, ...rawSession };
  }

  getParsedSession(): IUserSession | null {
    const rawSession = this.getSession();
    if (!rawSession) return null;
    return this.postProcessSession(rawSession);
  }

  private postProcessSession(session: IUserSessionRaw): IUserSession {
    const processedSession = { ...session };

    if (processedSession && !processedSession.meta_server) {
      processedSession.meta_server = processedSession.actual_web_server;
    }

    if (!isProduction && processedSession) {
      const urlsToPrefix = ['actual_web_server', 'web_server', 'meta_server'];
      urlsToPrefix.forEach((url) => {
        const processedSessionElement = processedSession[url];
        if (!processedSessionElement?.includes('proxy')) {
          processedSession[url] = '/proxy/' + processedSessionElement;
        }
      });
    }

    return snakeToCamel(processedSession);
  }

  toURLSearchParams(): URLSearchParams {
    return new URLSearchParams(this.session ?? {});
  }
}

export class UserSession {
  static session?: IUserSession | null;

  private static manager = new CoreSessionManager();

  static toURLSearchParams(): URLSearchParams {
    return UserSession.manager.toURLSearchParams();
  }

  static getSession(): IUserSession | null {
    return UserSession.manager.parsedSession;
  }
}
